import { IAxiosError } from '@/@types/AxiosError';
import { IGoogleRecaptchaInstance } from '@/@types/GoogleRecaptcha';
import { zodResolver } from '@hookform/resolvers/zod';
import { useFlags } from 'flagsmith/react';
import { Eye, EyeOff, Lock } from 'lucide-react';
import { useLayoutEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import QToolLogo from '@/assets/icons/qtool.svg';
import RecaptchaIcon from '@/assets/icons/recaptcha.svg';
import Button from '@/common/components/Button';
import { Checkbox } from '@/common/components/Checkbox';
import { LanguageMenuDropdown } from '@/common/components/LanguageMenuDropDown';
import { TextInput } from '@/common/components/TextInput';
import { useToast } from '@/common/hooks/useToast';
import { ENV } from '@/config/env';

import { useLogin } from '../../hooks/useLogin';
import { useLoginStatusStore } from '../../hooks/useLoginStatusStore';
import { SignInForm, SignInSchema } from '../../schemas/SignInSchema';

import { AcceptTermsOfUse } from './components/AcceptTermsOfUse';
import * as style from './styles';

export default function SignIn() {
  const [loadingCaptchaToken, setLoadingCaptchaToken] = useState(false);
  const [loadingSignIn, setLoadingSignIn] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { toast } = useToast();
  const { allow_sign_in } = useFlags(['allow_sign_in']);
  const { t } = useTranslation(['common', 'login/signIn']);
  const { signIn, identifyUser } = useLogin();
  const { setLoginStatus } = useLoginStatusStore();

  const captchaRef = useRef<IGoogleRecaptchaInstance>(null);

  const hookForm = useForm<SignInForm>({
    mode: 'onBlur',
    resolver: zodResolver(SignInSchema),
    defaultValues: {
      rememberData: true,
    },
  });

  const verifyCaptcha = async () => {
    try {
      if (!captchaRef?.current) {
        toast.warn(t('login/signIn:reCaptchaHumanError'));
        return null;
      }
      setLoadingCaptchaToken(true);
      captchaRef?.current?.reset();
      const reCaptchaToken = await captchaRef?.current?.executeAsync();
      return reCaptchaToken;
    } catch (error) {
      toast.error(t('login/signIn:reCaptchaAPIError'));
      return null;
    } finally {
      setLoadingCaptchaToken(false);
    }
  };

  const handleSignIn = async (data: SignInForm) => {
    const reCaptchaToken = await verifyCaptcha();

    if (!reCaptchaToken) {
      hookForm.setError('reCaptcha', {
        message: 'reCaptcha error',
      });
      return;
    }

    setLoadingSignIn(true);
    await signIn({
      ...data,
    });
    await identifyUser();

    setLoadingSignIn(false);
    setLoginStatus(true);
  };

  const handleToggleShowPassword = () => setShowPassword(!showPassword);

  useLayoutEffect(() => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (!window?.recaptchaOptions) return;

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    Object.assign(window.recaptchaOptions, {
      nonce: document
        ?.querySelector("meta[name='csp-nonce']")
        ?.getAttribute('content'),
    });
  }, []);

  useLayoutEffect(() => {
    window.HSStaticMethods.autoInit();
  }, [loadingSignIn]);

  return (
    <>
      <button
        data-hs-overlay="#hs-modal-terms-of-use"
        type="button"
        className="hidden"
      />
      <main className={style.main()}>
        <header className={style.header()}>
          <img src={QToolLogo} alt="QTool" />
        </header>

        <FormProvider {...hookForm}>
          <form
            className={style.form()}
            onSubmit={hookForm.handleSubmit(handleSignIn)}
          >
            <TextInput
              size="lg"
              name="username"
              placeholder={t('login/signIn:username')}
            />

            <TextInput
              size="lg"
              name="password"
              type={showPassword ? 'text' : 'password'}
              placeholder={t('login/signIn:password')}
              TrailingIcon={
                showPassword ? (
                  <Eye className="text-neutral-500" size={16} />
                ) : (
                  <EyeOff className="text-neutral-500" size={16} />
                )
              }
              handleClickTrailingIcon={handleToggleShowPassword}
            />

            <fieldset className={style.options()}>
              <Checkbox
                name="rememberData"
                label={t('login/signIn:rememberMe')}
                extraClassNames={style.checkbox()}
              />

              <div className={style.divider()} />

              <div className={style.wrapperLock()}>
                <Lock size={14} className={style.lock()} strokeWidth={3} />

                <Link
                  to={`/forgot-password`}
                  className={style.forgotPassword()}
                >
                  {t('login/signIn:forgotPassword')}
                </Link>
              </div>
            </fieldset>

            <fieldset className={style.reCaptchaContainer()}>
              <div
                className={style.reCaptcha({
                  hasError: !!hookForm.formState.errors.reCaptcha,
                })}
              >
                <Checkbox
                  name="reCaptcha"
                  label={t('common:recaptchaMessage')}
                  enableShowError={false}
                />

                <img src={RecaptchaIcon} alt="reCAPTCHA" />
              </div>

              {hookForm.formState.errors.reCaptcha && (
                <p className={style.reCaptchaError()}>
                  {t('login/signIn:reCaptchaError')}.
                </p>
              )}
            </fieldset>

            <Button
              type="submit"
              isLoading={loadingSignIn || loadingCaptchaToken}
              text={t('login/signIn:signIn')}
              disabled={!allow_sign_in.enabled}
              className={style.button()}
            />
          </form>
        </FormProvider>

        <footer className={style.footer()}>
          <LanguageMenuDropdown />

          <a
            href={ENV.VITE_TERMS_OF_USE_URL}
            target="_bank"
            className={style.privacyPolicy()}
          >
            {t('common:termsOfUseAndPrivacyPolicy')}
          </a>

          <p className={style.copyright()}>
            &copy; Copyright By IVECO - {t('login/signIn:allRightsReserved')}
          </p>

          <p className="text-center text-xs text-neutral-400">
            Powered by{' '}
            <a
              href="https://ideros.com.br/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 transition-colors"
            >
              IDEROS
            </a>
          </p>

          <p className={style.copyright()}>v{import.meta.env.APP_VERSION}</p>
        </footer>
      </main>

      <ReCAPTCHA
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        ref={captchaRef}
        sitekey={ENV.VITE_GOOGLE_RECAPTCHA_SITE_KEY}
        size="invisible"
        className="hidden"
      />

      <AcceptTermsOfUse recoverFormData={hookForm.getValues} />
    </>
  );
}
